import PropTypes from 'prop-types';
import FacilityUtils from 'utils/facility-utils';
import { RESERVATION_TYPES as TYPES } from 'utils/types/reservation';
const { PAYMENT_STATUSES, IN_OUT_MAPPING, RESERVATION_TYPES } = TYPES;
export const PROPTYPE_RESERVATION_VEHICLE = PropTypes.shape({
    licensePlateValue: PropTypes.string.isRequired,
    vehicleProfileId: PropTypes.string.isRequired,
    makeLabel: PropTypes.string,
    modelLabel: PropTypes.string,
    unlistedModel: PropTypes.bool,
});
export const PROPTYPE_RESERVATION_PAYMENT_STATUS = PropTypes.oneOf(Object.values(PAYMENT_STATUSES));
export const PROPTYPE_RESERVATION_EVENT = PropTypes.shape({
    title: PropTypes.string.isRequired,
});
export const PROPTYPE_RESERVATION_PRICE_BREAKDOWN = PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
        price: PropTypes.number.isRequired,
        shortDescription: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    totalPaid: PropTypes.number.isRequired,
    totalPossibleRefund: PropTypes.number.isRequired,
    totalPrice: PropTypes.number.isRequired,
});
export const PROPTYPE_RESERVATION_AMENITY = PropTypes.shape({
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
});
export const PROPTYPE_RESERVATION_MONTHLY_IN_OUT_DETAIL = PropTypes.shape({
    inOutAllowed: PropTypes.oneOf(Object.keys(IN_OUT_MAPPING)),
});
export const PROPTYPE_RESERVATION_IMAGE = PropTypes.shape({
    id: PropTypes.string.isRequired,
    urlTemplate: PropTypes.string.isRequired,
    alt: PropTypes.string,
});
export const PROPTYPE_RESERVATION_COMMUTER_DETAILS = PropTypes.shape({
    hasRate: PropTypes.bool,
    enterStart: PropTypes.string,
    enterEnd: PropTypes.string,
});
export const PROPTYPE_RESERVATION_ACCESS_HOURS = PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    days: PropTypes.string,
    type: PropTypes.string,
}));
export const PROPTYPE_RESERVATION_CITY = PropTypes.shape({
    slug: PropTypes.string.isRequired,
});
export const PROPTYPE_RESERVATION_FACILITY = PropTypes.shape({
    title: PropTypes.string.isRequired,
    barcodeType: PropTypes.string.isRequired,
    oversizeSupported: PropTypes.bool.isRequired,
    gettingHere: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(PROPTYPE_RESERVATION_IMAGE).isRequired,
    addresses: PropTypes.arrayOf(PropTypes.shape({
        city: PropTypes.string.isRequired,
        state: PropTypes.string.isRequired,
        streetAddress: PropTypes.string.isRequired,
        types: PropTypes.array.isRequired,
        zipcode: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    redemptionInstructions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        text: PropTypes.string.isRequired,
    }).isRequired).isRequired,
    id: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
    amenitiesFull: PropTypes.arrayOf(PROPTYPE_RESERVATION_AMENITY).isRequired,
    restrictions: PropTypes.arrayOf(PropTypes.string),
    monthlyInOutDetail: PROPTYPE_RESERVATION_MONTHLY_IN_OUT_DETAIL,
    reservationType: PropTypes.oneOf(Object.values(RESERVATION_TYPES)),
    phoneNumberRequired: PropTypes.bool.isRequired,
});
const PROPTYPE_RESERVATION_CARD = PropTypes.shape({
    externalId: PropTypes.string.isRequired,
    type: PropTypes.string,
    last4: PropTypes.string,
    wallet: PropTypes.string,
    paypalEmail: PropTypes.string,
    deleted: PropTypes.bool,
    isDefault: PropTypes.bool,
});
export const PROPTYPE_AIRPORT_SHUTTLE = PropTypes.shape({
    shuttle: PropTypes.shape({
        fastFrequency: PropTypes.number,
        slowFrequency: PropTypes.number,
        contactPhoneNumber: PropTypes.string,
        duration: PropTypes.number,
        type: PropTypes.string.isRequired,
        hours: PropTypes.shape({
            periods: PropTypes.arrayOf(PropTypes.shape({
                endDow: PropTypes.string.isRequired,
                endTime: PropTypes.string.isRequired,
                startDow: PropTypes.string.isRequired,
                startTime: PropTypes.string.isRequired,
            })),
            text: PropTypes.arrayOf(PropTypes.string),
        }),
        images: PROPTYPE_RESERVATION_IMAGE.isRequired,
    }),
});
export const PROPTYPE_AIRPORT_DETAILS = PropTypes.shape({
    details: PropTypes.shape({
        code: PropTypes.string.isRequired,
    }),
});
export const PROPTYPE_AIRPORT = PropTypes.shape({
    arrivalInstructions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        text: PropTypes.string.isRequired,
    })),
    departureInstructions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        text: PropTypes.string.isRequired,
    })),
    logos: PropTypes.arrayOf(PROPTYPE_RESERVATION_IMAGE),
    shuttle: PROPTYPE_AIRPORT_SHUTTLE,
    details: PROPTYPE_AIRPORT_DETAILS,
});
export const PROPTYPE_RESERVATION_PARTNER = PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    updateTimesEnabled: PropTypes.bool.isRequired,
    updateVehicleEnabled: PropTypes.bool.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    phoneNumberFormatted: PropTypes.string.isRequired,
});
export const PROPTYPE_RESERVATION = PropTypes.shape({
    timezone: PropTypes.string.isRequired,
    starts: PropTypes.object.isRequired,
    ends: PropTypes.object.isRequired,
    paymentStatus: PropTypes.string.isRequired,
    displayId: PropTypes.string.isRequired,
    rentalId: PropTypes.number.isRequired,
    city: PROPTYPE_RESERVATION_CITY.isRequired,
    facility: PROPTYPE_RESERVATION_FACILITY,
    vehicle: PROPTYPE_RESERVATION_VEHICLE,
    priceBreakdown: PROPTYPE_RESERVATION_PRICE_BREAKDOWN,
    vehicleRequired: PropTypes.bool.isRequired,
    spotheroCreditUsed: PropTypes.number.isRequired,
    discountAmount: PropTypes.number.isRequired,
    monthlyRecurring: PropTypes.bool,
    parkingPassType: PropTypes.string.isRequired,
    displayBarcode: PropTypes.bool.isRequired,
    stallName: PropTypes.string,
    canUpdateStartsAndEnds: PropTypes.bool.isRequired,
    event: PROPTYPE_RESERVATION_EVENT,
    accessHours: PROPTYPE_RESERVATION_ACCESS_HOURS.isRequired,
    commuterRateDetails: PROPTYPE_RESERVATION_COMMUTER_DETAILS.isRequired,
    accessHoursComment: PropTypes.string,
    rateTitle: PropTypes.string,
    currencyType: PropTypes.string.isRequired,
    creditCard: PROPTYPE_RESERVATION_CARD,
    airport: PROPTYPE_AIRPORT,
    ruleReservationType: PropTypes.string,
    cancellationAllowed: PropTypes.oneOf(Object.values(FacilityUtils.CANCELLATION_ALLOWED_ENUM)).isRequired,
    cancellationMinutes: PropTypes.number.isRequired,
    partner: PROPTYPE_RESERVATION_PARTNER,
});
export const PROPTYPE_RATE_DIFF = PropTypes.shape({
    originalRental: PropTypes.shape({
        price: PropTypes.number.isRequired,
        amountCharged: PropTypes.number.isRequired,
        discountAmount: PropTypes.number.isRequired,
        spotheroCreditUsed: PropTypes.number.isRequired,
    }).isRequired,
    priceBreakdown: PropTypes.shape({
        price: PropTypes.number.isRequired,
        chargeAmount: PropTypes.shape({
            charge: PropTypes.number.isRequired,
            credit: PropTypes.number.isRequired,
        }),
        refundAmount: PropTypes.shape({
            charge: PropTypes.number.isRequired,
            credit: PropTypes.number.isRequired,
        }),
    }),
    starts: PropTypes.string.isRequired,
    ends: PropTypes.string.isRequired,
    available: PropTypes.bool.isRequired,
});
